import { type Address, type PublicClient, type WalletClient, type Hex, type Hash, type Account, keccak256, encodeAbiParameters, parseAbiParameters } from 'viem';
import { X402FacilitatorABI } from '../abis/index.js';
import { validateAddress, validateRequired, validateDeployedAddress } from '../validators/index.js';
import { AAStarError } from '../errors/index.js';

/**
 * Computes the storage key for x402SettlementNonces[key].
 * Mirrors SuperPaymaster.x402NonceKey: keccak256(abi.encode(asset, from, nonce)).
 * Changed from raw nonce (pre P0-13) to triple-key (post P0-13).
 */
export function x402NonceKey(asset: Address, from: Address, nonce: Hex): Hex {
  return keccak256(encodeAbiParameters(
    parseAbiParameters('address, address, bytes32'),
    [asset, from, nonce]
  ));
}

/**
 * Checks if an x402 settlement nonce has been used.
 * Queries the triple-key slot: keccak256(abi.encode(asset, from, nonce)).
 * Replaces the pre-P0-13 pattern of querying x402SettlementNonces({ nonce }).
 */
export async function x402IsNonceUsed(
  client: PublicClient,
  superPaymasterAddress: Address,
  params: { asset: Address; from: Address; nonce: Hex }
): Promise<boolean> {
  const key = x402NonceKey(params.asset, params.from, params.nonce);
  const result = await client.readContract({
    address: superPaymasterAddress,
    abi: [
      {
        inputs: [{ internalType: 'bytes32', name: '', type: 'bytes32' }],
        name: 'x402SettlementNonces',
        outputs: [{ internalType: 'bool', name: '', type: 'bool' }],
        stateMutability: 'view',
        type: 'function',
      },
    ],
    functionName: 'x402SettlementNonces',
    args: [key],
  });
  return result as boolean;
}

export type X402Actions = {
    // Settlement
    settleX402Payment: (args: {
        from: Address, to: Address, asset: Address, amount: bigint,
        validAfter: bigint, validBefore: bigint, nonce: Hex, signature: Hex,
        account?: Account | Address
    }) => Promise<Hex>;
    settleX402PaymentDirect: (args: {
        from: Address, to: Address, asset: Address, amount: bigint, nonce: Hex,
        account?: Account | Address
    }) => Promise<Hex>;

    // View
    x402SettlementNonces: (args: { nonce: Hex }) => Promise<boolean>;
    facilitatorFeeBPS: () => Promise<bigint>;
    facilitatorEarnings: (args: { operator: Address, asset: Address }) => Promise<bigint>;
    operatorFacilitatorFees: (args: { operator: Address }) => Promise<bigint>;

    // Admin
    withdrawFacilitatorEarnings: (args: { asset: Address, account?: Account | Address }) => Promise<Hash>;
    setFacilitatorFeeBPS: (args: { fee: bigint, account?: Account | Address }) => Promise<Hash>;
    setOperatorFacilitatorFee: (args: { operator: Address, fee: bigint, account?: Account | Address }) => Promise<Hash>;
};

/**
 * x402 settlement actions. As of SP v5.4 these functions live on the standalone
 * X402Facilitator contract (NOT SuperPaymaster), so `address` MUST be the deployed
 * X402Facilitator address — passing the SuperPaymaster address will revert on-chain.
 */
export const x402Actions = (address: Address) => (client: PublicClient | WalletClient): X402Actions => {
    validateDeployedAddress(address, 'X402Facilitator');
    return ({
    // --- Settlement ---
    async settleX402Payment({ from, to, asset, amount, validAfter, validBefore, nonce, signature, account }) {
        try {
            validateAddress(from, 'from');
            validateAddress(to, 'to');
            validateAddress(asset, 'asset');
            validateRequired(nonce, 'nonce');
            validateRequired(signature, 'signature');
            return await (client as any).writeContract({
                address, abi: X402FacilitatorABI,
                functionName: 'settleX402Payment',
                args: [from, to, asset, amount, validAfter, validBefore, nonce, signature],
                account: account as any, chain: (client as any).chain
            });
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'settleX402Payment');
        }
    },

    async settleX402PaymentDirect({ from, to, asset, amount, nonce, account }) {
        try {
            validateAddress(from, 'from');
            validateAddress(to, 'to');
            validateAddress(asset, 'asset');
            validateRequired(nonce, 'nonce');
            return await (client as any).writeContract({
                address, abi: X402FacilitatorABI,
                functionName: 'settleX402PaymentDirect',
                args: [from, to, asset, amount, nonce],
                account: account as any, chain: (client as any).chain
            });
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'settleX402PaymentDirect');
        }
    },

    // --- View ---
    async x402SettlementNonces({ nonce }) {
        try {
            validateRequired(nonce, 'nonce');
            return await (client as PublicClient).readContract({
                address, abi: X402FacilitatorABI,
                functionName: 'x402SettlementNonces',
                args: [nonce]
            }) as boolean;
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'x402SettlementNonces');
        }
    },

    async facilitatorFeeBPS() {
        try {
            return await (client as PublicClient).readContract({
                address, abi: X402FacilitatorABI,
                functionName: 'facilitatorFeeBPS'
            }) as bigint;
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'facilitatorFeeBPS');
        }
    },

    async facilitatorEarnings({ operator, asset }) {
        try {
            validateAddress(operator, 'operator');
            validateAddress(asset, 'asset');
            return await (client as PublicClient).readContract({
                address, abi: X402FacilitatorABI,
                functionName: 'facilitatorEarnings',
                args: [operator, asset]
            }) as bigint;
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'facilitatorEarnings');
        }
    },

    async operatorFacilitatorFees({ operator }) {
        try {
            validateAddress(operator, 'operator');
            return await (client as PublicClient).readContract({
                address, abi: X402FacilitatorABI,
                functionName: 'operatorFacilitatorFees',
                args: [operator]
            }) as bigint;
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'operatorFacilitatorFees');
        }
    },

    // --- Admin ---
    async withdrawFacilitatorEarnings({ asset, account }) {
        try {
            validateAddress(asset, 'asset');
            return await (client as any).writeContract({
                address, abi: X402FacilitatorABI,
                functionName: 'withdrawFacilitatorEarnings',
                args: [asset],
                account: account as any, chain: (client as any).chain
            });
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'withdrawFacilitatorEarnings');
        }
    },

    async setFacilitatorFeeBPS({ fee, account }) {
        try {
            return await (client as any).writeContract({
                address, abi: X402FacilitatorABI,
                functionName: 'setFacilitatorFeeBPS',
                args: [fee],
                account: account as any, chain: (client as any).chain
            });
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'setFacilitatorFeeBPS');
        }
    },

    async setOperatorFacilitatorFee({ operator, fee, account }) {
        try {
            validateAddress(operator, 'operator');
            return await (client as any).writeContract({
                address, abi: X402FacilitatorABI,
                functionName: 'setOperatorFacilitatorFee',
                args: [operator, fee],
                account: account as any, chain: (client as any).chain
            });
        } catch (error) {
            throw AAStarError.fromViemError(error as Error, 'setOperatorFacilitatorFee');
        }
    },
});
};
